/**
 * Surface 2 — Tax Categories Management (Phase 3 UI)
 * Feb 2026 — Redesigned to match the Jurisdiction Directory design language
 * (unified Jurisdiction Management workspace). Adds KPI cards, search,
 * filters (state / status / in-use), and surfaces the business display
 * name as the primary visual element with the code as a secondary mono
 * chip. `code` is still IMMUTABLE post-create; `is_system=true` rows
 * cannot be deleted from the UI.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, Lock, Search, X, Tags } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  listTaxCategories, createTaxCategory,
  updateTaxCategory, deactivateTaxCategory,
} from '../utils/taxCollectorsApi';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import './JurisdictionDirectory.css';

const ROLES_WITH_WRITE = new Set(['admin', 'jurisdiction_manager']);

const emptyDraft = () => ({
  code: '', display_name: '', description: '',
  typical_for_states: [], typical_for_jurisdiction_types: [],
  sort_order: 100, is_active: true,
});

export default function TaxCategoriesPage() {
  const role = (() => {
    try { return (JSON.parse(localStorage.getItem('user') || '{}').role) || ''; }
    catch { return ''; }
  })();
  const canWrite = ROLES_WITH_WRITE.has(role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Filters
  const { searchInput, setSearchInput, debouncedValue: searchQuery, clearSearch }
    = useDebouncedSearch('', 350);
  const [stateFilter, setStateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | inactive | all
  const [usageFilter, setUsageFilter] = useState('all');      // all | in-use | unused

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTaxCategories({ include_counts: true });
      setItems(res.items || res || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load categories');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Distinct states across all categories (for the State filter dropdown).
  const distinctStates = useMemo(() => {
    const s = new Set();
    items.forEach(row => (row.typical_for_states || []).forEach(st => s.add(String(st).toUpperCase())));
    return Array.from(s).sort();
  }, [items]);

  // Apply client-side filters. Backend list is small enough (<100 rows)
  // that local filtering is fine and keeps the UX instant.
  const filteredItems = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    return items.filter(row => {
      // Search
      if (q) {
        const hay = [
          row.code, row.display_name, row.description,
          (row.typical_for_states || []).join(' '),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // State
      if (stateFilter) {
        const states = (row.typical_for_states || []).map(s => String(s).toUpperCase());
        if (states.length === 0) {
          // Empty = applies to all states, so it matches any state filter
          // (categories with no state restriction are universally valid).
        } else if (!states.includes(stateFilter)) {
          return false;
        }
      }
      // Status
      if (statusFilter === 'active' && !row.is_active) return false;
      if (statusFilter === 'inactive' && row.is_active) return false;
      // Usage
      const used = (row.usage_count ?? 0) > 0;
      if (usageFilter === 'in-use' && !used) return false;
      if (usageFilter === 'unused' && used) return false;
      return true;
    });
  }, [items, searchQuery, stateFilter, statusFilter, usageFilter]);

  // KPI metrics computed across the full (unfiltered) dataset so the
  // headline numbers stay stable while admins explore filters.
  const kpis = useMemo(() => {
    const total = items.length;
    const active = items.filter(r => r.is_active).length;
    const inUse = items.filter(r => (r.usage_count ?? 0) > 0).length;
    const stateSpecific = items.filter(r => (r.typical_for_states || []).length > 0).length;
    return { total, active, inUse, stateSpecific };
  }, [items]);

  const openCreate = () => { setEditing(null); setDraft(emptyDraft()); setModalOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setDraft({ ...emptyDraft(), ...row });
    setModalOpen(true);
  };

  const onSave = async () => {
    if (!editing) {
      if (!/^[a-z][a-z0-9_]{1,40}$/.test(draft.code)) {
        toast.error('Code must be lowercase alphanumeric/underscore (start with letter)');
        return;
      }
    }
    if (!draft.display_name?.trim()) {
      toast.error('Display name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { code, is_system, ...patch } = draft;
        await updateTaxCategory(editing.id, patch);
        toast.success('Category updated');
      } else {
        await createTaxCategory(draft);
        toast.success('Category created');
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const r = await deactivateTaxCategory(deleteTarget.id);
      toast.success(r?.deactivated ? 'Deactivated (in use)' : 'Deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const hasFilters = !!(searchQuery || stateFilter || statusFilter !== 'active' || usageFilter !== 'all');
  const resetFilters = () => {
    clearSearch();
    setStateFilter('');
    setStatusFilter('active');
    setUsageFilter('all');
  };

  return (
    <div className="jurisdiction-directory jx-premium" data-testid="tax-categories-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <span className="jx-head-icon" aria-hidden="true"><Tags /></span>
          <div>
          <h1 className="page-title">Tax Categories</h1>
          <p className="page-subtitle">
            Define the tax-period categories assignments can target (county, school, special assessments, etc.).
            Codes are permanent once created.
          </p>
          </div>
        </div>
        <div className="page-header-right">
          {canWrite && (
            <Button onClick={openCreate} data-testid="add-category-button">
              <Plus className="w-4 h-4" />
              New Category
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid" data-testid="tax-categories-kpi-grid">
        <div className="kpi-card kpi-accent-indigo" data-testid="kpi-total-categories">
          <p className="kpi-label">Total Categories</p>
          <div className="kpi-value">{kpis.total}</div>
          <p className="kpi-hint">All registry entries</p>
        </div>
        <div className="kpi-card kpi-accent-emerald" data-testid="kpi-active-categories">
          <p className="kpi-label">Active</p>
          <div className="kpi-value">{kpis.active}</div>
          <p className="kpi-hint">Available to assignments</p>
        </div>
        <div className="kpi-card kpi-accent-sky" data-testid="kpi-in-use-categories">
          <p className="kpi-label">In Use</p>
          <div className="kpi-value">{kpis.inUse}</div>
          <p className="kpi-hint">Referenced by ≥ 1 assignment</p>
        </div>
        <div className="kpi-card kpi-accent-amber" data-testid="kpi-state-specific-categories">
          <p className="kpi-label">State-Specific</p>
          <div className="kpi-value">{kpis.stateSpecific}</div>
          <p className="kpi-hint">Scoped to selected states</p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-input-wrap">
          <Search className="search-icon w-4 h-4" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by code, display name, description, or state…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="tax-category-search-input"
          />
        </div>

        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="filter-select"
          data-testid="tax-category-state-filter"
        >
          <option value="">All States</option>
          {distinctStates.map(st => <option key={st} value={st}>{st}</option>)}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
          data-testid="tax-category-status-filter"
        >
          <option value="active">Active Only</option>
          <option value="all">All Statuses</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <select
          value={usageFilter}
          onChange={(e) => setUsageFilter(e.target.value)}
          className="filter-select"
          data-testid="tax-category-usage-filter"
        >
          <option value="all">All Usage</option>
          <option value="in-use">In Use</option>
          <option value="unused">Not In Use</option>
        </select>

        {hasFilters && (
          <Button variant="outline" onClick={resetFilters} data-testid="tax-category-reset-filters">
            <X className="w-4 h-4" />
            Reset
          </Button>
        )}
      </div>

      {/* Stats */}
      {!loading && (
        <div className="stats-bar">
          <span className="stat-item">
            <strong>{filteredItems.length}</strong>{' '}
            {filteredItems.length === 1 ? 'category' : 'categories'}
            {hasFilters && items.length !== filteredItems.length && (
              <span> &nbsp;·&nbsp; {items.length} total</span>
            )}
          </span>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading tax categories…</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏷️</div>
          <h3 className="empty-state-title">No tax categories found</h3>
          <p className="empty-state-description">
            {hasFilters
              ? 'Try adjusting your filters to see more results.'
              : 'Create your first tax category to start tagging assignments.'}
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="jurisdictions-table" data-testid="tax-categories-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Description</th>
                <th>States</th>
                <th style={{ textAlign: 'right' }}># Used By</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(row => (
                <tr key={row.id} data-testid={`tax-category-row-${row.id}`}>
                  <td>
                    <div className="jurisdiction-name">{row.display_name}</div>
                    <span className="code-chip">
                      {row.is_system && <Lock className="lock-icon w-3 h-3" aria-label="System category" />}
                      {row.code}
                    </span>
                  </td>
                  <td>
                    <div style={{ fontSize: '13px', color: '#475569', maxWidth: '360px' }}>
                      {row.description || <span style={{ color: '#9ca3af' }}>—</span>}
                    </div>
                  </td>
                  <td>
                    {(row.typical_for_states || []).length > 0
                      ? <span style={{ fontSize: '13px', color: '#334155' }}>{row.typical_for_states.join(', ')}</span>
                      : <span style={{ fontSize: '12px', color: '#94a3b8' }}>All</span>}
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#0f172a' }}>
                    {row.usage_count ?? 0}
                  </td>
                  <td>
                    {row.is_active
                      ? <span className="status-badge status-active">Active</span>
                      : <span className="status-badge status-inactive">Inactive</span>}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canWrite ? (
                      <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(row)}
                          data-testid={`edit-category-${row.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!row.is_system && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(row)}
                            data-testid={`delete-category-${row.id}`}
                            className="text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>read only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Tax Category' : 'New Tax Category'}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Categories let assignments target specific tax types (e.g., county, school).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 px-1">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Code *
                {editing && (
                  <span className="ml-2 text-xs text-slate-400" title="Code is permanent once created">
                    (locked)
                  </span>
                )}
              </Label>
              <Input
                value={draft.code}
                disabled={!!editing}
                onChange={e => setDraft(d => ({ ...d, code: e.target.value.toLowerCase() }))}
                placeholder="e.g. county, school, special_assessment"
                className="font-mono"
                data-testid="category-code-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Display Name *</Label>
              <Input value={draft.display_name || ''}
                onChange={e => setDraft(d => ({ ...d, display_name: e.target.value }))}
                data-testid="category-display-name-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500 mb-1.5 block">Description</Label>
              <Textarea rows={2} value={draft.description || ''}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Typical for states (comma sep, blank = all)
              </Label>
              <Input value={(draft.typical_for_states || []).join(', ')}
                onChange={e => setDraft(d => ({
                  ...d,
                  typical_for_states: e.target.value
                    .split(/[,|]/).map(s => s.trim().toUpperCase()).filter(Boolean)
                }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Typical for jurisdiction types (comma sep)
              </Label>
              <Input value={(draft.typical_for_jurisdiction_types || []).join(', ')}
                onChange={e => setDraft(d => ({
                  ...d,
                  typical_for_jurisdiction_types: e.target.value
                    .split(/[,|]/).map(s => s.trim().toLowerCase()).filter(Boolean)
                }))} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Sort order</Label>
              <Input type="number" value={draft.sort_order ?? 100}
                onChange={e => setDraft(d => ({ ...d, sort_order: Number(e.target.value) || 0 }))} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={!!draft.is_active}
                onCheckedChange={v => setDraft(d => ({ ...d, is_active: v }))}
                id="cat-is-active" />
              <Label htmlFor="cat-is-active" className="text-sm">Active</Label>
            </div>
          </div>
          <DialogFooter className="border-t border-slate-200 pt-4">
            <Button variant="outline" onClick={() => setModalOpen(false)}
              data-testid="category-cancel-button">Cancel</Button>
            <Button onClick={onSave} disabled={saving}
              data-testid="category-save-button">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="category-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete or deactivate this category?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.display_name}: if any assignment references this category,
              it will be soft-deactivated; otherwise it will be permanently deleted.
              System categories cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onConfirmDelete}
              data-testid="category-delete-confirm-button"
              className="bg-rose-600 hover:bg-rose-700">
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
